import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  Chip,
  Stack,
  TextField,
  MenuItem,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Description as QuoteIcon,
} from '@mui/icons-material';
import QuotationService from '../../services/quotation.service';

const QuotationList: React.FC = () => {
  const navigate = useNavigate();
  const [quotations, setQuotations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchQuotations();
  }, [statusFilter]);

  const fetchQuotations = async () => {
    try {
      setLoading(true);
      const params: any = {
        populate: ['customer', 'insuranceClient', 'createdBy'],
        'pagination[pageSize]': 100,
        sort: 'date:desc',
      };

      if (statusFilter !== 'all') {
        params['filters[status][$eq]'] = statusFilter;
      }

      const response = await QuotationService.getAll(params);
      setQuotations(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching quotations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this quotation?')) {
      try {
        await QuotationService.delete(id);
        fetchQuotations();
      } catch (error) {
        console.error('Error deleting quotation:', error);
      }
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'accepted':
        return 'success';
      case 'sent':
        return 'info';
      case 'draft':
        return 'warning';
      case 'rejected':
        return 'error';
      case 'expired':
        return 'default';
      default:
        return 'default';
    }
  };

  const formatCurrency = (amount: number) => {
    if (!amount) return 'OMR 0.00';
    return `OMR ${amount.toFixed(2)}`;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const filteredQuotations = quotations.filter((quote) => {
    const insuranceClient = quote.attributes?.insuranceClient?.data?.attributes || quote.attributes?.customer?.data?.attributes;
    const searchLower = searchTerm.toLowerCase();
    return (
      quote.attributes?.quotationNumber?.toLowerCase().includes(searchLower) ||
      insuranceClient?.customerName?.toLowerCase().includes(searchLower) ||
      insuranceClient?.companyName?.toLowerCase().includes(searchLower) ||
      insuranceClient?.contactPerson?.toLowerCase().includes(searchLower) ||
      insuranceClient?.vinNumber?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          <QuoteIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Quotations
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/quotations/create')}
        >
          New Quotation
        </Button>
      </Stack>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            fullWidth
            label="Search..."
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by quotation number or insurance client"
          />
          <TextField
            select
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="all">All Status</MenuItem>
            <MenuItem value="draft">Draft</MenuItem>
            <MenuItem value="sent">Sent</MenuItem>
            <MenuItem value="accepted">Accepted</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
            <MenuItem value="expired">Expired</MenuItem>
          </TextField>
        </Stack>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Quotation #</TableCell>
              <TableCell>Insurance Client</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Valid Until</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : filteredQuotations.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No quotations found
                </TableCell>
              </TableRow>
            ) : (
              filteredQuotations.map((quotation) => {
                const attrs = quotation.attributes;
                const insuranceClient = attrs?.insuranceClient?.data?.attributes || attrs?.customer?.data?.attributes;

                return (
                  <TableRow key={quotation.id} hover>
                    <TableCell>
                      <Typography variant="body1" fontWeight="medium">
                        {attrs?.quotationNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {insuranceClient?.customerName || insuranceClient?.companyName || insuranceClient?.contactPerson || '-'}
                    </TableCell>
                    <TableCell>{formatDate(attrs?.date)}</TableCell>
                    <TableCell>
                      {formatDate(attrs?.validUntil)}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body1" fontWeight="bold" color="primary">
                        {formatCurrency(attrs?.total)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={attrs?.status || 'draft'}
                        color={getStatusColor(attrs?.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="View">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/quotations/${quotation.id}`)}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/quotations/${quotation.id}/edit`)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(quotation.id)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default QuotationList;

